import { useEffect, useRef, useState } from 'react';
import { MessageCircle, Bot, X, Send, RotateCcw } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { API } from '@/lib/api';

const SESSION_KEY = 'wiyak-chat-session';
const HISTORY_KEY = 'wiyak-chat-history';
function renderMd(text) {
  return text.split('\n').map((line, i) => {
    const bullet = /^\s*[-•*]\s+/.test(line);
    const parts = line.replace(/^\s*[-•*]\s+/, '').split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith('**') && p.endsWith('**') ? <b key={j}>{p.slice(2, -2)}</b> : p);
    return <span key={i} className={`block ${bullet ? 'ps-3 relative before:absolute before:start-0 before:content-["•"]' : ''}`}>{parts}</span>;
  });
}

const newSession = () => `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

function useSession() {
  const [session, setSession] = useState(() => localStorage.getItem(SESSION_KEY) || newSession());
  useEffect(() => { localStorage.setItem(SESSION_KEY, session); }, [session]);
  return [session, () => setSession(newSession())];
}

export function WhatsAppButton() {
  const { t } = useLanguage();
  const number = String(t('settings.whatsapp') || '').replace(/\D/g, '');
  if (t('settings.showWhatsapp') === false || !number) return null;
  return (
    <a href={`https://wa.me/${number}`} target="_blank" rel="noreferrer" data-testid="floating-whatsapp-btn" aria-label={t('assistant.whatsapp')}
      className="group flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_18px_40px_-12px_rgba(37,211,102,0.7)] transition-transform duration-300 hover:scale-105">
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor" aria-hidden="true"><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" /></svg>
    </a>
  );
}

export function ChatWidget() {
  const { t, lang, isRTL } = useLanguage();
  const [open, setOpen] = useState(false);
  const [session, resetSession] = useSession();
  const [msgs, setMsgs] = useState(() => { try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; } });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => { localStorage.setItem(HISTORY_KEY, JSON.stringify(msgs.slice(-40))); }, [msgs]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs, open]);

  if (t('assistant.enabled') === false) return null;

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput('');
    setMsgs((m) => [...m, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session_id: session, message: q, lang }) });
      if (!res.ok || !res.body) throw new Error('bad response');
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let failed = false;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith('data:')) continue;
          const ev = JSON.parse(p.slice(5));
          if (ev.delta) setMsgs((m) => { const n = [...m]; n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + ev.delta }; return n; });
          if (ev.error) failed = true;
        }
      }
      if (failed) throw new Error('assistant error');
    } catch {
      setMsgs((m) => { const n = [...m]; if (!n[n.length - 1].content) n[n.length - 1] = { role: 'assistant', content: t('assistant.error'), error: true }; return n; });
    } finally { setBusy(false); }
  };

  const reset = () => { resetSession(); setMsgs([]); };

  return (
    <>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="chat-launcher-btn" aria-label={t('assistant.open')}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-blue text-white shadow-[0_18px_40px_-12px_rgba(0,82,255,0.7)] transition-transform duration-300 hover:scale-105">
        {open ? <X className="h-6 w-6" /> : <Bot className="h-7 w-7" />}
      </button>

      {open && (
        <div className="fixed bottom-24 end-4 z-[95] flex h-[min(560px,calc(100vh-7rem))] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-[1.75rem] border border-brand-line bg-white shadow-[0_40px_90px_-30px_rgba(15,23,42,0.45)]" data-testid="chat-widget">
          <div className="flex items-center gap-3 bg-brand-ink px-4 py-3.5 text-white">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-blue"><Bot className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-sm font-extrabold" data-testid="chat-title">{t('assistant.title')}</p>
              <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-white/60"><span className="h-1.5 w-1.5 rounded-full bg-brand-emerald" /> {t('hero.live')}</p>
            </div>
            <button type="button" onClick={reset} title={t('assistant.reset')} data-testid="chat-reset-btn" className="grid h-8 w-8 place-items-center rounded-lg text-white/70 hover:bg-white/10"><RotateCcw className="h-4 w-4" /></button>
            <button type="button" onClick={() => setOpen(false)} data-testid="chat-close-btn" className="grid h-8 w-8 place-items-center rounded-lg text-white/70 hover:bg-white/10"><X className="h-4 w-4" /></button>
          </div>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto bg-brand-paper px-4 py-4" data-lenis-prevent data-testid="chat-messages">
            <div className="max-w-[88%] rounded-2xl rounded-ss-md bg-white px-4 py-3 text-sm leading-relaxed text-brand-ink shadow-sm" data-testid="chat-welcome">{t('assistant.welcome')}</div>
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-2" data-testid="chat-suggestions">
                {t('assistant.suggestions').map((s, i) => (
                  <button key={i} type="button" onClick={() => send(s)} data-testid={`chat-suggestion-${i}`} className="rounded-full border border-brand-blue/30 bg-white px-3 py-1.5 text-xs font-bold text-brand-blue hover:bg-brand-blue/5">{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.role}-${i}`}>
                <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${m.role === 'user' ? 'rounded-se-md bg-brand-blue text-white' : m.error ? 'rounded-ss-md bg-brand-crimson/10 text-brand-crimson' : 'rounded-ss-md bg-white text-brand-ink'}`}>
                  {m.content ? renderMd(m.content) : <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-dim" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-dim [animation-delay:120ms]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-dim [animation-delay:240ms]" /></span>}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-brand-line bg-white p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('assistant.placeholder')} data-testid="chat-input"
              className="flex-1 rounded-full border border-brand-line bg-brand-paper px-4 py-2.5 text-sm font-semibold text-brand-ink outline-none focus:border-brand-blue" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-btn" aria-label={t('assistant.send')} className="grid h-10 w-10 place-items-center rounded-full bg-brand-blue text-white disabled:opacity-40">
              <Send className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default function FloatingActions() {
  return (
    <div className="fixed bottom-4 end-4 z-[95] flex flex-col items-center gap-3" data-testid="floating-actions">
      <WhatsAppButton />
      <ChatWidget />
    </div>
  );
}
